import type { PortalMe, PortalPerson } from '@bbbk/schema';
import { toIrishNational } from '@bbbk/schema';
import { Avatar } from '../components/common';
import { G, ProviderMark } from './glyphs';
import { WhatsAppMark } from './MessagePatricia';
import { Tag } from './Tag';
import { whatsappHref } from './whatsapp';
import { PrimarySubmit, StepBar, StepFailure, StepPage, type StepError } from './RegistrationChrome';

// `c2b` / `c2c` — Your details, step 1 of 2 (AC8–AC15).
//
// Who this is and what we hold, on one screen. The person the link was issued
// to is pre-selected and locked (open question 3, ruled by the merge): a
// choice that has already been made is not offered. The others on the
// household are drawn as the prototype draws them, greyed and not choosable.
// One locked field, the mobile the link went to; NO email field — the sign-in
// address is already on the pick row (AC13).

export interface DetailsDraft {
  firstName: string;
  lastName: string;
  address: string;
}

export interface RegistrationDetailsProps {
  me: PortalMe;
  draft: DetailsDraft;
  onChange: (draft: DetailsDraft) => void;
  onSubmit: () => void;
  busy: boolean;
  error: StepError | null;
}

/** The prototype's avatars carry ONE initial (G24); the desk's carry two. */
export function initialOf(name: string): string {
  return (name.trim()[0] ?? '?').toUpperCase();
}

function firstNameOf(person: PortalPerson): string {
  return person.name.trim().split(/\s+/)[0] ?? person.name;
}

function OwnRow({ me }: { me: PortalMe }) {
  const self = me.contacts.find((p) => p.id === me.contact.id);
  const name = me.contact.firstName ?? (self ? firstNameOf(self) : me.contactName);
  const signedIn = me.contact.email ?? '';
  return (
    <div className="cp-choice on" aria-current="true">
      <Avatar name={name} id={String(me.contact.id)} size={36} initials={initialOf(name)} />
      <span className="txt">
        <b>{name}</b>
        <small>
          <span className="signed">
            {me.contact.provider && <ProviderMark provider={me.contact.provider} />}
            Signed in · {signedIn}
          </span>
        </small>
      </span>
      <span className="box radio">{G.tick({ size: 13, strokeWidth: 3 })}</span>
    </div>
  );
}

function TakenRow({ person }: { person: PortalPerson }) {
  const registered = person.registeredAt !== null;
  return (
    <div className="cp-choice taken" aria-disabled="true">
      <Avatar name={person.name} id={String(person.id)} size={36} initials={initialOf(person.name)} />
      <span className="txt">
        <b>{firstNameOf(person)}</b>
        {registered && <small>Already registered</small>}
      </span>
      {registered && <Tag tone="neutral">Registered</Tag>}
    </div>
  );
}

export function RegistrationDetails({ me, draft, onChange, onSubmit, busy, error }: RegistrationDetailsProps) {
  const mobile = toIrishNational(me.contact.phone);
  const others = me.contacts.filter((p) => p.id !== me.contact.id);
  const firstNameBad = error?.kind === 'field' && error.field === 'firstName';
  const set = (patch: Partial<DetailsDraft>) => onChange({ ...draft, ...patch });

  return (
    <StepPage onSubmit={onSubmit}>
      <StepBar step={1} />
      <h1 className="cp-h1">Your details</h1>
      <p className="cp-p cp-lead">Tell us which of you this is, then add the rest.</p>

      <div className="cp-list">
        <OwnRow me={me} />
        {others.map((p) => (
          <TakenRow key={p.id} person={p} />
        ))}
      </div>

      <div className="cp-row cp-names">
        <div className="cp-field">
          <label className="ico" htmlFor="reg-first">
            {G.user({ size: 14 })}First name
          </label>
          <input
            id="reg-first"
            className={'cp-input' + (firstNameBad ? ' is-bad' : '')}
            name="firstName"
            autoComplete="given-name"
            value={draft.firstName}
            onChange={(e) => set({ firstName: e.target.value })}
            aria-invalid={firstNameBad ? 'true' : undefined}
            aria-describedby={firstNameBad ? 'reg-first-why' : undefined}
          />
          {firstNameBad && (
            <p className="cp-p mute cp-fieldnote cp-why" id="reg-first-why">
              Tell us your first name.
            </p>
          )}
        </div>
        <div className="cp-field">
          <label className="ico" htmlFor="reg-last">
            {G.users({ size: 14 })}Last name
          </label>
          <input
            id="reg-last"
            className="cp-input"
            name="lastName"
            autoComplete="family-name"
            placeholder="Last name"
            value={draft.lastName}
            onChange={(e) => set({ lastName: e.target.value })}
          />
        </div>
      </div>

      <div className="cp-field cp-mobile">
        <label className="ico">{G.phone({ size: 14 })}Mobile</label>
        <div className="cp-input is-locked" aria-readonly="true">
          {mobile ? (
            <>
              <span className="cp-prefix">{mobile.prefix}</span>
              <span>{mobile.digits}</span>
            </>
          ) : (
            <span className="ph">No number on file</span>
          )}
          <span className="cp-lock">{G.lock({ size: 14 })}</span>
        </div>
        <p className="cp-p mute cp-fieldnote">
          <span>To change this number,</span>
          <a
            className="wa-inline"
            href={whatsappHref('the mobile on my registration is wrong — it should be ')}
            target="_blank"
            rel="noopener noreferrer"
          >
            <WhatsAppMark size={13} />
            <u>WhatsApp Patricia</u>
          </a>
        </p>
      </div>

      <div className="cp-field">
        <label className="ico" htmlFor="reg-address">
          {G.pin({ size: 14 })}Address
        </label>
        <textarea
          id="reg-address"
          className="cp-input cp-area"
          name="address"
          autoComplete="street-address"
          placeholder="Street, town, county"
          value={draft.address}
          onChange={(e) => set({ address: e.target.value })}
          rows={3}
        />
      </div>

      <StepFailure error={error} />
      <div className="cp-actions">
        <PrimarySubmit busy={busy} icon={G.tick({ size: 15 })}>
          Submit your details
        </PrimarySubmit>
      </div>
    </StepPage>
  );
}
